import { useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useEffect, useRef, useState } from 'react';
import ActivityIndicatorScreen from '../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../components/AlertBox';
import { useUserContext } from '../../../context/UserContext';
import { useStudyEventFormDataSourceContext } from '../../../screens/study/StudyEventFormDataSource';
import {
  finishWithScreenFailed,
  isScreenFailedEnabled,
  ParticipantStatusChangeConfig
} from '../../../screens/study/helpers/ParticipantStatusChangeHelpers';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';

interface ParticipantStatusChangeElementConfig {
  participantStatusChange?: ParticipantStatusChangeConfig;
}

type ParticipantStatusChangeProps<T> = FormElementProps<
  T,
  ParticipantStatusChangeElementConfig
>;

export const ParticipantStatusChange = <T extends object>({
  element
}: ParticipantStatusChangeProps<T>) => {
  const {
    emitEvent,
    formValues,
    studyParticipantId: contextStudyParticipantId,
    stateMachine
  } = useDynamicFormContext<any, any>();
  const { user, studyParticipants, updateUserSession } = useUserContext();
  const navigation = useNavigation<StackNavigationProp<any>>();
  const route = useRoute();
  const eventForm = useStudyEventFormDataSourceContext();
  const routeStudyParticipantId = (
    route.params as { studyParticipantId?: number }
  )?.studyParticipantId;
  const studyParticipantId =
    formValues?.studyParticipantId ??
    contextStudyParticipantId ??
    eventForm?.studyParticipantId ??
    routeStudyParticipantId;
  const [error, setError] = useState('');
  const handledRef = useRef(false);

  useEffect(() => {
    const handleStatusChange = async () => {
      if (!isScreenFailedEnabled(element.config)) {
        setError(
          'No participant status change configured. Please contact the study administrator.'
        );
        return;
      }
      if (handledRef.current) return;
      if (!studyParticipantId) {
        setError(
          'Unable to determine study participant. Please go back and try again.'
        );
        return;
      }
      setError(prev =>
        prev ===
        'Unable to determine study participant. Please go back and try again.'
          ? ''
          : prev
      );
      handledRef.current = true;
      try {
        await finishWithScreenFailed({
          navigation,
          user,
          studyParticipants,
          studyParticipantId,
          statePath: stateMachine?.currentState?.path,
          completeEvent: eventForm?.completeEvent,
          emitEvent,
          updateUserSession
        });
      } catch (err) {
        handledRef.current = false;
        setError('Unable to connect to server. Please try again later.');
        // eslint-disable-next-line no-console
        console.warn(
          '[ParticipantStatusChange] failed to complete screen-failed form',
          err
        );
      }
    };
    // wait till next render loop since StudyEventFormDataSource doesn't
    // register the 'final' subscribe immediately
    const timeoutId = setTimeout(() => handleStatusChange());
    return () => clearTimeout(timeoutId);
  }, [
    element.config,
    stateMachine?.currentState?.path,
    navigation,
    emitEvent,
    studyParticipantId,
    updateUserSession,
    user,
    studyParticipants,
    eventForm?.completeEvent
  ]);

  return error ? (
    <AlertBox severity='error'>{error}</AlertBox>
  ) : (
    <ActivityIndicatorScreen backgroundColor='transparent' />
  );
};

export const ParticipantStatusChangePlugin: FormElementPlugin<
  any,
  ParticipantStatusChangeElementConfig
> = {
  name: 'Participant Status Change',
  type: 'ParticipantStatusChange',
  element: ParticipantStatusChange as React.FC<
    FormElementProps<any, ParticipantStatusChangeElementConfig>
  >
};
